import React, { useMemo, useRef } from "react";
import {
  Button,
  Card,
  ConfigProvider,
  Empty,
  Input,
  Modal,
  Pagination,
  Result,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  BookOutlined,
  FilePdfOutlined,
  LeftOutlined,
  PlayCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { reviTheme } from "../../../../theme/reviTheme";
import { useTherapistDocumentacionController } from "./useTherapistDocumentacionController";

import { useMovil } from "../../../../hooks/useIsNarrow";
import { FilaTocable } from "../../../../components/revi/FilaTocable";
import "./Page_Therapist_Documentacion.scss";

function getVideoEmbedUrl(rawUrl) {
  if (!rawUrl) return null;

  // YouTube
  const ytMatch =
    rawUrl.match(/youtube\.com\/watch\?v=([^&]+)/i) ||
    rawUrl.match(/youtu\.be\/([^?&]+)/i);
  if (ytMatch?.[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  // Vimeo
  const vimeoMatch = rawUrl.match(/vimeo\.com\/(\d+)/i);
  if (vimeoMatch?.[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  return null;
}

function ResourceStrip({ items, loading, error, onOpen, kind }) {
  const { t } = useTranslation(["therapist", "common"]);
  const viewportRef = useRef(null);
  const movil = useMovil();

  const scrollBy = (delta) => {
    if (!viewportRef.current) return;
    viewportRef.current.scrollBy({ left: delta, behavior: "smooth" });
  };

  if (loading) {
    return <Skeleton active paragraph={{ rows: 3 }} />;
  }

  if (error) {
    return (
      <Result
        status="error"
        title={t("documentacion.errorCarga")}
        subTitle={error}
      />
    );
  }

  if (!items?.length) {
    return <Empty description={t("documentacion.tira.sinResultados")} />;
  }

  /**
   * En movil, lista vertical. Una tira que se desliza en horizontal DENTRO de
   * una pagina que ya se desplaza en vertical obliga a adivinar en que
   * direccion se arrastra cada zona, y deja la mitad de los recursos fuera de
   * la vista sin decir que estan ahi.
   *
   * Aqui no se explora un catalogo: se busca una guia concreta por su titulo, y
   * son entre diez y veinte. Una lista los muestra todos y se recorre en la
   * misma direccion que el resto de la pagina.
   */
  if (movil) {
    return (
      <div className="revi-resource-list">
        {items.map((it) => (
          <FilaTocable
            key={it.id}
            as={Card}
            alto={96}
            className="revi-resource-row"
            onTocar={() => onOpen(kind, it)}
            aria-label={it.title}
            hoverable={false}
          >
            <div className="revi-resource-row__fila">
              {it.image ? (
                <img
                  className="revi-resource-row__mini"
                  alt=""
                  src={it.image}
                  loading="lazy"
                />
              ) : (
                <div className="revi-resource-row__mini revi-resource-row__mini--vacia">
                  {kind === "video" ? (
                    <PlayCircleOutlined />
                  ) : (
                    <FilePdfOutlined />
                  )}
                </div>
              )}

              <div className="revi-resource-row__texto">
                <span className="revi-resource-row__titulo">{it.title}</span>
                <span className="revi-resource-row__meta">
                  {kind === "video"
                    ? t("documentacion.tira.etiquetaVideo")
                    : t("documentacion.tira.etiquetaPdf")}
                  {it.description ? ` \u00b7 ${it.description}` : ""}
                </span>
              </div>
            </div>
          </FilaTocable>
        ))}
      </div>
    );
  }

  return (
    <div className="revi-resource-strip">
      <Button
        aria-label={t("documentacion.tira.desplazarIzquierda")}
        icon={<LeftOutlined />}
        onClick={() => scrollBy(-420)}
      />
      <div
        ref={viewportRef}
        className="revi-resource-strip__viewport"
        role="list"
      >
        {items.map((it) => (
          <Card
            key={it.id}
            hoverable
            className="revi-resource-card"
            onClick={() => onOpen(kind, it)}
            cover={
              it.image ? (
                <img
                  className="revi-resource-card__cover"
                  alt={it.title}
                  src={it.image}
                />
              ) : (
                <div className="revi-resource-card__cover revi-resource-card__cover--placeholder">
                  {kind === "video" ? (
                    <PlayCircleOutlined />
                  ) : (
                    <FilePdfOutlined />
                  )}
                </div>
              )
            }
          >
            <div className="revi-resource-card__meta">
              <Tooltip title={it.title}>
                <Typography.Text strong ellipsis>
                  {it.title}
                </Typography.Text>
              </Tooltip>

              <div className="revi-resource-card__tags">
                <Tag
                  icon={
                    kind === "video" ? (
                      <PlayCircleOutlined />
                    ) : (
                      <FilePdfOutlined />
                    )
                  }
                >
                  {kind === "video"
                    ? t("documentacion.tira.etiquetaVideo")
                    : t("documentacion.tira.etiquetaPdf")}
                </Tag>
              </div>

              <Typography.Paragraph
                className="revi-resource-card__desc"
                ellipsis={{ rows: 2, tooltip: it.description }}
              >
                {it.description}
              </Typography.Paragraph>
            </div>
          </Card>
        ))}
      </div>
      <Button
        aria-label={t("documentacion.tira.desplazarDerecha")}
        icon={<RightOutlined />}
        onClick={() => scrollBy(420)}
      />
    </div>
  );
}

/**
 * Pagina de Formacion del portal REVI.
 *
 * La misma pagina sirve al terapeuta y a la institucion: cambia la audiencia
 * (que material recibe) y los textos de cabecera. La gestion de los recursos
 * se hace desde el panel admin (/admin/revi?tab=formacion).
 */
export function Page_Therapist_Documentacion({
  audience = "therapist",
  title,
  subtitle,
  infoTitle,
}) {
  const { t } = useTranslation(["therapist", "common"]);

  // La cabecera por defecto es la del terapeuta y sale del fichero de idioma.
  // Quien reutiliza la pagina (la institucion) sigue pudiendo pasar la suya.
  const tituloPagina = title ?? t("documentacion.titulo");
  const movil = useMovil();
  const subtituloPagina = subtitle ?? t("documentacion.subtitulo");
  const tituloInfo = infoTitle ?? t("documentacion.info.titulo");

  const {
    searchInput,
    videos,
    pdfs,
    infos,
    videoPage,
    pdfPage,
    preview,
    actions,
  } = useTherapistDocumentacionController(audience);

  const videoPagination = videos.pagination;
  const pdfPagination = pdfs.pagination;

  const previewTitle = preview?.item?.title || "";

  const videoEmbedUrl = useMemo(() => {
    if (preview.type !== "video") return null;
    return getVideoEmbedUrl(preview.item?.url);
  }, [preview.type, preview.item]);

  return (
    <ConfigProvider theme={reviTheme}>
      <div className="revi-page-documentacion">
        <div className="revi-page-documentacion__main">
          <div className="revi-page-documentacion__header">
            <Typography.Title
              level={2}
              className="revi-page-documentacion__title"
            >
              <BookOutlined /> {tituloPagina}
            </Typography.Title>
            <Typography.Paragraph
              type="secondary"
              className="revi-page-documentacion__subtitle revi-subtitulo-pagina"
            >
              {subtituloPagina}
            </Typography.Paragraph>
          </div>

          <Card className="revi-page-documentacion__search" size="small">
            <Input.Search
              value={searchInput}
              onChange={(e) => actions.setSearchInput(e.target.value)}
              placeholder={t("documentacion.buscarPlaceholder")}
              allowClear
            />
          </Card>

          <div className="revi-page-documentacion__carousels">
            <Card
              title={t("documentacion.videos.titulo")}
              size="small"
              extra={
                videoPagination ? (
                  <Typography.Text type="secondary">
                    {t("common:contadores.resultados", {
                      count: videoPagination.totalDocs,
                    })}
                  </Typography.Text>
                ) : null
              }
            >
              <ResourceStrip
                items={videos.items}
                loading={videos.loading}
                error={videos.error}
                onOpen={actions.openPreview}
                kind="video"
              />

              {videoPagination ? (
                <div className="revi-page-documentacion__pagination">
                  <Pagination
                    current={videoPage}
                    pageSize={videoPagination.limit}
                    total={videoPagination.totalDocs}
                    onChange={(p) => actions.setVideoPage(p)}
                    showSizeChanger={false}
                  />
                </div>
              ) : null}
            </Card>

            <Card
              title={t("documentacion.pdfs.titulo")}
              size="small"
              extra={
                pdfPagination ? (
                  <Typography.Text type="secondary">
                    {t("common:contadores.resultados", {
                      count: pdfPagination.totalDocs,
                    })}
                  </Typography.Text>
                ) : null
              }
            >
              <ResourceStrip
                items={pdfs.items}
                loading={pdfs.loading}
                error={pdfs.error}
                onOpen={actions.openPreview}
                kind="pdf"
              />

              {pdfPagination ? (
                <div className="revi-page-documentacion__pagination">
                  <Pagination
                    current={pdfPage}
                    pageSize={pdfPagination.limit}
                    total={pdfPagination.totalDocs}
                    onChange={(p) => actions.setPdfPage(p)}
                    showSizeChanger={false}
                  />
                </div>
              ) : null}
            </Card>
          </div>

          <Card title={tituloInfo} size="small">
            {(() => {
              const info = infos.items?.[0]; // ✅ ÚNICA (ocupa todo el ancho)

              if (infos.loading)
                return <Skeleton active paragraph={{ rows: 4 }} />;
              if (infos.error)
                return (
                  <Result
                    status="error"
                    title={t("documentacion.errorCarga")}
                    subTitle={infos.error}
                  />
                );
              if (!info)
                return <Empty description={t("documentacion.info.vacio")} />;

              return (
                <div className="revi-info-hero">
                  {info.image ? (
                    <div className="revi-info-hero__media">
                      <img
                        className="revi-info-hero__img"
                        alt={info.title}
                        src={info.image}
                      />
                    </div>
                  ) : null}

                  <div className="revi-info-hero__content">
                    <Typography.Title
                      level={4}
                      className="revi-info-hero__title"
                    >
                      {info.title}
                    </Typography.Title>
                    <Typography.Paragraph className="revi-info-hero__desc">
                      {info.description}
                    </Typography.Paragraph>
                  </div>
                </div>
              );
            })()}
          </Card>
        </div>

        <Modal
          open={preview.open}
          title={previewTitle}
          onCancel={actions.closePreview}
          footer={null}
          width={980}
          // Solo en movil: en escritorio el visor se abre donde siempre.
          centered={movil}
          className="revi-preview-modal"
          destroyOnHidden
        >
          {preview.type === "video" ? (
            <div className="revi-preview">
              {videoEmbedUrl ? (
                <iframe
                  className="revi-preview__frame"
                  src={videoEmbedUrl}
                  title={previewTitle}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <Result
                  status="info"
                  title={t("documentacion.preview.videoNoEmbebible")}
                  subTitle={t("documentacion.preview.videoNoEmbebibleAyuda")}
                  extra={
                    <Button
                      type="primary"
                      onClick={() =>
                        window.open(
                          preview.item?.url,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      {t("documentacion.preview.abrirVideo")}
                    </Button>
                  }
                />
              )}
            </div>
          ) : preview.type === "pdf" ? (
            <div className="revi-preview">
              <div className="revi-preview__actions">
                <Button
                  type="primary"
                  onClick={() =>
                    window.open(
                      preview.item?.file,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  {t("documentacion.preview.abrirPdf")}
                </Button>
              </div>
              {/*
                El PDF no se incrusta en movil: Safari en iOS no pinta un PDF
                dentro de un iframe y deja un rectangulo blanco de 520 px que
                parece un fallo de carga. El boton de arriba lo abre en una
                pestana, donde el visor del sistema si sabe mostrarlo.
              */}
              {!movil && preview.item?.file ? (
                <iframe
                  className="revi-preview__frame"
                  src={preview.item.file}
                  title={previewTitle}
                />
              ) : !preview.item?.file ? (
                <Empty description={t("documentacion.preview.sinPdf")} />
              ) : null}
            </div>
          ) : null}
        </Modal>
      </div>
    </ConfigProvider>
  );
}
